import graphql from "babel-plugin-relay/macro";
import { useMemo } from "react";
import { useFragment } from "react-relay";

import {
  BookingAnalyticsData,
  PublicPaymentInformation,
  PrivatePaymentInformation,
  PublicBookingInformation,
  PrivateBookingInformation,
  GolfBookingInformation,
  ActivityManagementBookingInformation,
  DiningBookingInformation,
} from "@/screens/BookingDetail/NewPatronBookingFlow/types";

import {
  DepartmentType,
  FacilityType,
  useGetBookingAnalyticsMetadata_booking$key,
} from "./__generated__/useGetBookingAnalyticsMetadata_booking.graphql";

export const useGetBookingAnalyticsMetadata = (
  bookingRef: useGetBookingAnalyticsMetadata_booking$key | null
) => {
  const booking = useFragment(
    useGetBookingAnalyticsMetadataFragmentString,
    bookingRef
  );

  const analyticsData: BookingAnalyticsData = useMemo(() => {
    if (!booking) return null;

    const isPublicBooking =
      !!booking.startSlot?.agenda?.facility?.department?.supportsPublic;

    const paymentInformation:
      | PublicPaymentInformation
      | PrivatePaymentInformation = isPublicBooking
      ? {
          requiresPrePayment:
            booking.startSlot?.agenda?.facility?.publicBookingPermissionSet
              ?.prePaymentRequirement ?? "UNKNOWN",
          whooshBookingFee:
            booking.startSlot?.agenda?.facility?.whooshBookingFee ?? 0,
          whooshRoundFee:
            booking.startSlot?.agenda?.facility?.whooshRoundFee ?? 0,
        }
      : null;

    const playerInformation:
      | PublicBookingInformation
      | PrivateBookingInformation = isPublicBooking
      ? {
          playerCount: booking.rounds?.edges?.length ?? 0,
          knownCustomerCount:
            booking.rounds?.edges?.filter((edge) => edge?.node?.player?.id)
              .length ?? 0,
          unknownCustomerCount:
            booking.rounds?.edges?.filter((edge) => !edge?.node?.player?.id)
              .length ?? 0,
        }
      : {
          playerCount: booking.rounds?.edges?.length ?? 0,
          knownMemberCount:
            booking.rounds?.edges?.filter(
              (edge) =>
                edge?.node?.__typename === "MemberRound" &&
                edge?.node?.player?.id
            ).length ?? 0,
          unknownMemberCount:
            booking.rounds?.edges?.filter(
              (edge) =>
                edge?.node?.__typename === "MemberRound" &&
                !edge?.node?.player?.id
            ).length ?? 0,
          knownGuestCount:
            booking.rounds?.edges?.filter(
              (edge) =>
                edge?.node?.__typename === "GuestRound" &&
                edge?.node?.player?.id
            ).length ?? 0,
          unknownGuestCount:
            booking.rounds?.edges?.filter(
              (edge) =>
                edge?.node?.__typename === "GuestRound" &&
                !edge?.node?.player?.id
            ).length ?? 0,
        };

    const bookingDepartmentType: DepartmentType | undefined =
      booking.startSlot?.agenda?.facility?.department?.type;
    const facilityType: FacilityType | undefined =
      booking.startSlot?.agenda?.facility?.type;

    const activityTypeMetadata:
      | GolfBookingInformation
      | ActivityManagementBookingInformation
      | DiningBookingInformation =
      facilityType === "GOLF_COURSE"
        ? {
            holeCounts: {
              9:
                booking.rounds?.edges?.filter(
                  (e) => e?.node?.courseLayout?.holeCount === "NINE"
                )?.length ?? 0,
              18:
                booking.rounds?.edges?.filter(
                  (e) => e?.node?.courseLayout?.holeCount === "EIGHTEEN"
                )?.length ?? 0,
            },
            transportation: {
              CADDIE:
                booking.rounds?.edges?.filter(
                  (e) => e?.node?.transportationType?.code === "CADDIE"
                )?.length ?? 0,
              CADDIE_CART:
                booking.rounds?.edges?.filter(
                  (e) => e?.node?.transportationType?.code === "CADDIE_CART"
                )?.length ?? 0,
              CART:
                booking.rounds?.edges?.filter(
                  (e) => e?.node?.transportationType?.code === "CART"
                )?.length ?? 0,
              FULL_CART:
                booking.rounds?.edges?.filter(
                  (e) => e?.node?.transportationType?.code === "FULL_CART"
                )?.length ?? 0,
              MOTOR_PULL_CART:
                booking.rounds?.edges?.filter(
                  (e) => e?.node?.transportationType?.code === "MOTOR_PULL_CART"
                )?.length ?? 0,
              PULL_CART:
                booking.rounds?.edges?.filter(
                  (e) => e?.node?.transportationType?.code === "PULL_CART"
                )?.length ?? 0,
              PRIVATE_CART:
                booking.rounds?.edges?.filter(
                  (e) => e?.node?.transportationType?.code === "PRIVATE_CART"
                )?.length ?? 0,
              SINGLE_CART:
                booking.rounds?.edges?.filter(
                  (e) => e?.node?.transportationType?.code === "SINGLE_CART"
                )?.length ?? 0,
              WALK:
                booking.rounds?.edges?.filter(
                  (e) => e?.node?.transportationType?.code === "WALK"
                )?.length ?? 0,
              OTHER:
                booking.rounds?.edges?.filter(
                  (e) => !e?.node?.transportationType?.code
                )?.length ?? 0,
            },
          }
        : facilityType === "DINING"
        ? {
            partySize: booking.partySize ?? null,
            dietaryTags: booking.dietaryTags?.filter((tag) => !!tag) ?? [],
            occasionTags: booking.occasionTags?.filter((tag) => !!tag) ?? [],
            notesFromMember: booking.notesFromMember ?? null,
          }
        : {
            duration: booking.durationMins ?? 0,
          };

    const data: BookingAnalyticsData = {
      clubId: booking.startSlot?.agenda?.facility?.department?.club?.id ?? "",
      clubSlug:
        booking.startSlot?.agenda?.facility?.department?.club?.slug ?? "",
      bookingStatus: booking.status ?? "UNKNOWN",
      isRequest: booking.startSlot?.type === "QUEUE",
      bookingType: booking.type,
      facilityType: booking.startSlot?.agenda?.facility?.type,
      bookingDepartmentType,
      isPublicBooking:
        !!booking.startSlot?.agenda?.facility?.department?.supportsPublic,
      paymentInformation,
      playerInformation,
      isGolf: facilityType === "GOLF_COURSE",
      activityTypeMetadata,
    };

    return data;
  }, [booking]);

  return analyticsData;
};

export const useGetBookingAnalyticsMetadataFragmentString = graphql`
  fragment useGetBookingAnalyticsMetadata_booking on Booking {
    type
    startSlot {
      type
      agenda {
        facility {
          type
          whooshBookingFee
          whooshRoundFee
          publicBookingPermissionSet {
            prePaymentRequirement
          }
          department {
            type
            supportsPublic
            club {
              id
              slug
            }
          }
        }
      }
    }
    ... on StandardBooking {
      status
      durationMins
    }
    ... on LessonBooking {
      status
      durationMins
    }
    ... on DiningBooking {
      status
      partySize
      dietaryTags
      occasionTags
      notesFromMember
    }
    rounds {
      edges {
        node {
          __typename
          courseLayout {
            holeCount
          }
          transportationType {
            code
          }
          player {
            id
          }
        }
      }
    }
  }
`;
